import * as Sentry from '@sentry/react';
import ButtonComponent from 'core/modules/atoms/Button';
import IconComponent from 'core/modules/atoms/Icon';
import React, { Component, ErrorInfo, ReactNode } from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { Loading } from 'ui';

// Type assertions for JS components without TypeScript definitions
const Button = ButtonComponent as React.ComponentType<{
  text?: string;
  color?: string;
  size?: string;
  onClick?: () => void;
}>;

const Icon = IconComponent as React.ComponentType<{
  type?: string;
  size?: string;
  color?: string;
}>;

interface Props extends WithTranslation {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  shouldAutoReload: boolean;
}

function isChunkLoadError(error: Error): boolean {
  const message = error?.message?.toLowerCase() ?? '';
  return (
    message.includes('loading chunk') ||
    message.includes('loading css chunk') ||
    message.includes('failed to fetch dynamically imported module') ||
    message.includes('error loading dynamically imported module') ||
    error?.name === 'ChunkLoadError'
  );
}

class ChunkLoadErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, shouldAutoReload: false };
  }

  static getDerivedStateFromError(error: Error): Partial<State> | null {
    if (!isChunkLoadError(error)) return null;
    const key = `chunk_reload_${globalThis.location.pathname}`;
    const alreadyReloaded = !!sessionStorage.getItem(key);
    return alreadyReloaded ? { hasError: true, shouldAutoReload: false } : { hasError: false, shouldAutoReload: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    if (!isChunkLoadError(error)) {
      throw error;
    }

    const key = `chunk_reload_${globalThis.location.pathname}`;
    if (!sessionStorage.getItem(key)) {
      sessionStorage.setItem(key, '1');
      globalThis.location.reload();
      return;
    }

    Sentry.withScope((scope) => {
      scope.setTag('error_type', 'chunk_load_error');
      scope.setTag('route', globalThis.location.pathname);
      scope.setExtra('componentStack', errorInfo.componentStack);
      scope.setExtra('url', globalThis.location.href);
      scope.setExtra('userAgent', navigator.userAgent);
      scope.setLevel('error');
      Sentry.captureException(error);
    });

    console.error('[ChunkLoadErrorBoundary] Persistent chunk load error after reload:', {
      message: error.message,
      route: globalThis.location.pathname,
    });
  }

  handleReload = (): void => {
    sessionStorage.removeItem(`chunk_reload_${globalThis.location.pathname}`);
    globalThis.location.reload();
  };

  render(): ReactNode {
    const { hasError, shouldAutoReload } = this.state;
    const { children, fallback, t } = this.props;

    if (shouldAutoReload) {
      return <Loading />;
    }

    if (hasError) {
      if (fallback) {
        return fallback;
      }

      return (
        <div className="chunk-error">
          <div className="chunk-error__content">
            <div className="chunk-error__icon">
              <Icon type="warning-circle-fill" size="xxl" color="primary" />
            </div>

            <h2 className="chunk-error__title">{t('error:Error loading page')}</h2>

            <p className="chunk-error__message">{t('error:Page load error message')}</p>

            <div className="chunk-error__actions">
              <Button text={t('error:Reload page')} color="primary" size="medium" onClick={this.handleReload} />
            </div>
          </div>
        </div>
      );
    }

    return <>{children}</>;
  }
}

export default withTranslation()(ChunkLoadErrorBoundary);
